import { useMemo } from 'react';
import { OrderVolumeProfileDataState } from 'widgets/orderVolumeProfile/store/data/OrderVolumeProfileDataState.ts';
import { useOrderVolumeProfileWidgetStreamedData } from 'widgets/orderVolumeProfile/hooks/useOrderVolumeProfileStreamedData.ts';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData.ts';
import {
    BucketedDataItem,
    createEmptyBuckets,
    findNextBucket,
} from 'utils/timeseries/bucket.ts';
import { OrderVolumeProfileTimeSeriesData } from 'widgets/orderVolumeProfile/api/OrderVolumeProfileElement.ts';
import { getItemTime } from 'utils/timeseries/timeSeriesUtils.ts';
import { useOrderVolumeProfileConfigSlice } from 'widgets/orderVolumeProfile/store/configuration/configSlice.ts';

export type OrderVolumeProfileBucketedDataItem = TimeSeriesChartDataItem &
    BucketedDataItem & {
        _i: number;
        vol: number;
        auctVol: number;
        contVol: number;
        cumVol: number;
        wgt: number;
        auctWgt: number;
        contWgt: number;
        cumWgt: number;
    };

export type OrderVolumeProfileData = {
    bucketedData: OrderVolumeProfileBucketedDataItem[];
    startTimestamp: number;
    endTimestamp: number;
};

const initialData: OrderVolumeProfileData = {
    bucketedData: [],
    startTimestamp: 0,
    endTimestamp: 0,
};
export function buildOrderVolumeProfileData(
    data: OrderVolumeProfileDataState | undefined,
    barSizeInMinutes: number
): OrderVolumeProfileData {
    if (!data || data.timeSeries.length === 0) {
        return initialData;
    }

    const { timeSeries } = data;

    const timeSeriesWithVolumeData = timeSeries.filter(
        (t) => t.vol !== undefined
    );

    const sortedTimeSeries = timeSeriesWithVolumeData.sort((a, b) =>
        a._i > b._i ? +1 : -1
    );

    const bucketedData = bucket(barSizeInMinutes, sortedTimeSeries);
    const startTimestamp = bucketedData[0].startTime;
    const endTimestamp = bucketedData[bucketedData.length - 1].endTime;

    return { bucketedData, startTimestamp, endTimestamp };
}

export function useOrderVolumeProfileData(): OrderVolumeProfileData {
    const data = useOrderVolumeProfileWidgetStreamedData()?.data;
    const {
        config: { barSizeInMinutes },
    } = useOrderVolumeProfileConfigSlice();

    return useMemo(
        () => buildOrderVolumeProfileData(data, barSizeInMinutes),
        [barSizeInMinutes, data]
    );
}

function bucket(
    bucketSizeInMinutes: number,
    sortedData: OrderVolumeProfileTimeSeriesData[]
): OrderVolumeProfileBucketedDataItem[] {
    const bucketedData = createEmptyBuckets<OrderVolumeProfileBucketedDataItem>(
        bucketSizeInMinutes,
        sortedData,
        [],
        buildEmptyBucket
    );

    let currentBucket: OrderVolumeProfileBucketedDataItem | undefined =
        bucketedData[0];

    for (const d of sortedData) {
        if (!currentBucket) {
            break;
        }
        const dataTime = getItemTime(d);

        while (dataTime > currentBucket.endTime) {
            const previousBucket = currentBucket;
            currentBucket = findNextBucket(bucketedData, currentBucket);
            if (!currentBucket) {
                break;
            }
            currentBucket.cumVol = previousBucket.cumVol;
            currentBucket.cumWgt = previousBucket.cumWgt;
        }

        if (currentBucket) {
            currentBucket.vol = currentBucket.vol + (d.vol ?? 0);
            currentBucket.auctVol = currentBucket.auctVol + (d.auctVol ?? 0);
            currentBucket.contVol = currentBucket.contVol + (d.contVol ?? 0);

            // Multiple weights to become percentages
            currentBucket.wgt = currentBucket.wgt + (d.wgt ?? 0) * 100;
            currentBucket.auctWgt =
                currentBucket.auctWgt + (d.auctWgt ?? 0) * 100;
            currentBucket.contWgt =
                currentBucket.contWgt + (d.contWgt ?? 0) * 100;

            // Last non-zero cumulated field in a bucket wins...
            if (d.cumVol !== 0) {
                currentBucket.cumVol = d.cumVol;
            }
            if (d.cumWgt !== 0) {
                currentBucket.cumWgt = d.cumWgt * 100;
            }
        }
    }

    return bucketedData;
}

function buildEmptyBucket(
    startTime: number,
    _endTime: number
): Partial<OrderVolumeProfileBucketedDataItem> {
    return {
        _i: startTime / 60000,
        time: startTime / 60000,
        vol: 0,
        auctVol: 0,
        contVol: 0,
        cumVol: 0,
        wgt: 0,
        auctWgt: 0,
        contWgt: 0,
        cumWgt: 0,
    };
}
